import {
  P360_FIRST_FYEAR,
  PLAN_REQUEST_FOR_PREV_YEARS,
} from 'legacy/lut/components/widgets/public/partner360/planning/config'
import {
  selectActualsTypeWithDefault,
  selectFinancialYear,
  selectPartnerAccess,
  selectProgramType,
} from 'legacy/lut/features/partner360/configuration'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useGetPlanningQuery } from 'partner360/entities/planning'

import { getMdfContraMetrics } from '../lib/getMdfContraMetrics'
import { selectMdfActivitiesLoadedContext } from '../model/selectors/selectMdfActivitiesLoadedContext'
import { IMdfActivity } from '../model/types'

import { useMdfDashboardFiltersForm } from './useMdfDashboardFiltersForm'

export const useMdfContraMetrics = (partnerId: string, activities: IMdfActivity[]) => {
  const fYear = useSelector(selectFinancialYear)
  const programType = useSelector(selectProgramType)
  const actualsType = useSelector(selectActualsTypeWithDefault)
  const { isRegionalDistributor } = useSelector(selectPartnerAccess)
  const { timeframesFilterValue } = useMdfDashboardFiltersForm()
  const mdfContext = useSelector(selectMdfActivitiesLoadedContext)
  const isMdfContextLoaded =
    mdfContext?.partnerId === partnerId &&
    mdfContext.fYear === fYear &&
    mdfContext.programType === programType

  // Match KI Editor's existing query arguments to reuse its RTK Query cache.
  // currentData prevents sales from a previous partner/year/offering flashing
  // while the current context is loading.
  const { currentData: planningData, isError } = useGetPlanningQuery(
    {
      partnerId,
      fYear,
      programType,
      fromFYear: Math.max(fYear - PLAN_REQUEST_FOR_PREV_YEARS, P360_FIRST_FYEAR),
      isRegionalDistributor,
    },
    { skip: !partnerId || !fYear || !programType }
  )

  return useMemo(() => {
    const metrics = getMdfContraMetrics(activities, isError ? [] : (planningData?.Planning ?? []), {
      partnerId,
      fYear,
      programType,
      actualsType,
      timeframes: timeframesFilterValue ?? [],
    })

    // The MDF slice can still contain the previous offering during a context change.
    return { ...metrics, contraPercent: isMdfContextLoaded ? metrics.contraPercent : null }
  }, [
    activities,
    planningData?.Planning,
    isError,
    partnerId,
    fYear,
    programType,
    actualsType,
    timeframesFilterValue,
    isMdfContextLoaded,
  ])
}
